import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  ArrowLeft, Truck, Percent, Snowflake, Flame, Ban, Ticket, ChevronDown, ChevronUp, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import OrderDetailsModal from './OrderDetailsModal';
import RentalDetailsModal from './RentalDetailsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMPONENT_META = {
  delivery_fees: { icon: Truck, color: '#FF6B35' },
  commissions: { icon: Percent, color: '#004E64' },
  ice: { icon: Snowflake, color: '#2EC4B6' },
  shisha: { icon: Flame, color: '#9B5DE5' },
  penalties: { icon: Ban, color: '#E63946' },
  discounts: { icon: Ticket, color: '#9FA2B4' },
};

const fmt = (v) => `${v < 0 ? '−' : ''}€${Math.abs(v || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }) + ' ' +
      d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  } catch (e) { return '—'; }
};

const ComponentCard = ({ comp, language, onItemClick }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = COMPONENT_META[comp.key] || { icon: TrendingUp, color: '#004E64' };
  const Icon = meta.icon;
  const label = language === 'pt' ? comp.label : (comp.label_en || comp.label);
  const isNegative = comp.total < 0;

  return (
    <Card className="rounded-2xl border-0 shadow-sm" data-testid={`platform-revenue-comp-${comp.key}`}>
      <CardContent className="p-4">
        <button
          type="button"
          className="w-full flex items-center justify-between text-left"
          onClick={() => comp.count > 0 && setExpanded(e => !e)}
          data-testid={`platform-revenue-comp-toggle-${comp.key}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${meta.color}1A` }}>
              <Icon size={20} style={{ color: meta.color }} />
            </div>
            <div>
              <p className="font-medium text-[#004E64] text-sm">{label}</p>
              <p className="text-xs text-[#9FA2B4]">
                {comp.count} {language === 'pt' ? (comp.count === 1 ? 'movimento' : 'movimentos') : (comp.count === 1 ? 'entry' : 'entries')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-lg font-bold ${isNegative ? 'text-red-500' : 'text-[#004E64]'}`}>{fmt(comp.total)}</span>
            {comp.count > 0 && (expanded ? <ChevronUp size={16} className="text-[#9FA2B4]" /> : <ChevronDown size={16} className="text-[#9FA2B4]" />)}
          </div>
        </button>

        {expanded && (
          <div className="mt-3 border-t pt-2 max-h-64 overflow-y-auto">
            {comp.items.map((item, idx) => (
              <button
                key={`${item.id}-${idx}`}
                type="button"
                onClick={() => onItemClick(item.id)}
                className="w-full flex items-center justify-between py-1.5 text-sm border-b border-gray-50 last:border-0 text-left hover:bg-[#F4F1EA] rounded-lg px-1 transition-colors"
                data-testid={`platform-revenue-item-${item.id}-${idx}`}
                title={language === 'pt' ? 'Ver detalhes do pedido' : 'View order details'}
              >
                <div className="min-w-0">
                  <p className="text-[#004E64] truncate">{item.label}</p>
                  <p className="text-xs text-[#9FA2B4]">{fmtDate(item.date)} · {item.id}</p>
                </div>
                <span className={`font-medium whitespace-nowrap ml-3 ${item.amount < 0 ? 'text-red-500' : 'text-[#004E64]'}`}>{fmt(item.amount)}</span>
              </button>
            ))}
            {comp.count > comp.items.length && (
              <p className="text-xs text-[#9FA2B4] pt-2">
                {language === 'pt' ? `A mostrar os ${comp.items.length} mais recentes de ${comp.count}` : `Showing latest ${comp.items.length} of ${comp.count}`}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PlatformRevenueBreakdown = ({ token, language, period, customStart, customEnd, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailRental, setDetailRental] = useState(null);

  const openEntity = async (entityId) => {
    try {
      if (String(entityId).startsWith('rental_')) {
        const res = await fetch(`${API_URL}/api/shisha-rentals/${entityId}`);
        if (!res.ok) throw new Error();
        setDetailRental(await res.json());
      } else {
        const res = await fetch(`${API_URL}/api/orders/${entityId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error();
        setDetailOrder(await res.json());
      }
    } catch (e) {
      toast.error(language === 'pt' ? 'Não foi possível abrir os detalhes' : 'Could not open details');
    }
  };

  const fetchBreakdown = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (customStart && customEnd) {
        params.set('start_date', customStart);
        params.set('end_date', customEnd);
      } else {
        params.set('period', period);
      }
      const res = await fetch(`${API_URL}/api/admin/analytics/platform-revenue?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.debug('platform revenue fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [token, period, customStart, customEnd]);

  useEffect(() => { fetchBreakdown(); }, [fetchBreakdown]);

  const periodLabel = customStart && customEnd
    ? `${customStart} → ${customEnd}`
    : period === 'today' ? (language === 'pt' ? 'Hoje' : 'Today')
    : period === 'week' ? (language === 'pt' ? 'Últimos 7 dias' : 'Last 7 days')
    : (language === 'pt' ? 'Últimos 30 dias' : 'Last 30 days');

  return (
    <div className="space-y-4" data-testid="platform-revenue-breakdown-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" className="rounded-full" onClick={onBack} data-testid="platform-revenue-back-btn">
          <ArrowLeft size={16} className="mr-1" />
          {language === 'pt' ? 'Voltar' : 'Back'}
        </Button>
        <div>
          <h2 className="text-lg font-bold text-[#004E64]">{language === 'pt' ? 'Receita da Plataforma — Detalhe' : 'Platform Revenue — Breakdown'}</h2>
          <p className="text-xs text-[#9FA2B4]">{periodLabel}</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-16 rounded-2xl" />
          <Skeleton className="h-16 rounded-2xl" />
        </div>
      ) : !data ? (
        <p className="text-sm text-[#9FA2B4]">{language === 'pt' ? 'Erro ao carregar dados' : 'Error loading data'}</p>
      ) : (
        <>
          <Card className="rounded-2xl border-0 shadow-sm bg-[#FF6B35]/5">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-[#9FA2B4]">{language === 'pt' ? 'Total que fica para a WEGOO' : 'Total kept by WEGOO'}</p>
                <p className="text-3xl font-bold text-[#FF6B35]" data-testid="platform-revenue-total">{fmt(data.total)}</p>
                <p className="text-xs text-[#9FA2B4] mt-1">
                  {language === 'pt'
                    ? 'Tudo o que não é repassado aos restaurantes'
                    : 'Everything not owed to restaurants'}
                </p>
              </div>
              <Badge className="bg-[#FF6B35] text-white rounded-full">{periodLabel}</Badge>
            </CardContent>
          </Card>

          <div className="space-y-3">
            {data.components.map(comp => (
              <ComponentCard key={comp.key} comp={comp} language={language} onItemClick={openEntity} />
            ))}
          </div>

          <p className="text-xs text-[#9FA2B4]">
            {language === 'pt'
              ? 'Soma: taxas de entrega + comissões de 15% sobre a comida + gelo e shishas (100% WEGOO) + penalizações de cancelamento retidas − cupões suportados pela plataforma. O valor da comida dos restaurantes (85%) não está incluído.'
              : 'Sum: delivery fees + 15% food commissions + ice & shisha (100% WEGOO) + retained cancellation penalties − platform-funded coupons. Restaurant food value (85%) is not included.'}
          </p>
        </>
      )}
      <OrderDetailsModal
        order={detailOrder}
        open={!!detailOrder}
        onOpenChange={(o) => { if (!o) setDetailOrder(null); }}
        token={token}
        onUpdate={fetchBreakdown}
      />
      <RentalDetailsModal
        rental={detailRental}
        open={!!detailRental}
        onOpenChange={(o) => { if (!o) setDetailRental(null); }}
        token={token}
        language={language}
        onUpdate={fetchBreakdown}
      />
    </div>
  );
};

export default PlatformRevenueBreakdown;
